<template>
  <component
    :is="isLinked ? NuxtLink:'div'"
    v-editable="blok"
    :to="linkUrl"
    :target="linkTarget"
    class="PromoBlock block relative w-full group"
    :class="desktopWidth('full')"
  >
    <div
      class="relative overflow-hidden"
    >
      <div
        v-if="blok.asset && blok.asset.filename"
        class="w-full h-full relative top-0 left-0
        transition-all duration-500 opacity-100"
        :class="[blok.mobileAspectRatio, {
          'desk:hidden': blok.desktopAsset && blok.desktopAsset.filename,
          'group-hover:opacity-80': isLinked
        }]"
      >
        <video
          v-if="checkIfVideoFile(blok.asset.filename)"
          width="w-full"
          height="h-full"
          class="w-full h-full object-cover"
          loop
          muted
          autoplay
          playsinline
        >
          <source :src="blok.asset.filename" type="video/mp4">
        </video>
        <nuxt-img
          v-else-if="blok.asset && blok.asset.filename"
          preset="standard"
          loading="lazy"
          :sizes="`sm:100vw desk:${imageSize}`"
          class="h-full w-full object-cover"
          :class="blok.focalPoint"
          :src="blok.asset.filename"
          :alt="blok.asset.alt"
        />
      </div>

      <div
        v-if="blok.desktopAsset && blok.desktopAsset.filename"
        class="hidden desk:block w-full h-full relative top-0 left-0
        transition-transform duration-500"
        :class="blok.desktopAspectRatio"
      >
        <video
          v-if="checkIfVideoFile(blok.desktopAsset.filename)"
          width="w-full"
          height="h-full"
          class="w-full h-full object-cover"
          loop
          muted
          autoplay
          playsinline
        >
          <source :src="blok.desktopAsset.filename" type="video/mp4">
        </video>
        <nuxt-img
          v-else-if="blok.desktopAsset && blok.desktopAsset.filename"
          preset="standard"
          loading="lazy"
          :sizes="`sm:100vw desk:${imageSize}`"
          class="h-full w-full object-cover"
          :class="blok.desktopFocalPoint"
          :src="blok.desktopAsset.filename"
          :alt="blok.desktopAsset.alt"
        />
      </div>
      <div
        class="absolute top-12 left-12 w-64 h-64"
        :class="[topSplashSize, topSplashPosition, { 'spin': blok.spinSplash, }]"
      >
        <div
          v-if="blok.splashTopText || blok.splashText"
          class="h-full w-full rounded-full flex flex-col items-center justify-center gap-2 text-text-inverse text-center"
          :class="{
            'bg-bg-inverse': blok.splashBackgroundColor === 'default',
            'bg-bg-errorStrong': blok.splashBackgroundColor === 'sale',
          }"
        >
          <div
            v-if="blok.splashTopText"
            class="mb-2 -mt-2"
            :class="[discountTextSize]"
          >
            {{ blok.splashTopText }}
          </div>
          <div
            v-if="blok.splashText"
            class="leading-single"
            :class="[discountNumberSize]"
          >
            {{ blok.splashText }}
          </div>
        </div>
        <div v-else class="w-full h-full rounded-full overflow-hidden">
          <nuxt-img
            v-if="blok.splashImage && blok.splashImage.filename"
            preset="standard"
            loading="lazy"
            :sizes="`sm:100vw desk:${imageSize}`"
            :src="blok.splashImage.filename"
            :alt="blok.splashImage.alt"
          />
        </div>
      </div>
      <div
        class="absolute bottom-12 right-12 w-64 h-64"
        :class="[topSplashSize, bottomSplashPosition, { 'spin': blok.spinDiscountSplash, }]"
      >
        <div
          v-if="blok.splashDiscountTopText || blok.splashDiscountText"
          class="h-full w-full rounded-full flex flex-col items-center justify-center gap-2 text-text-inverse text-center"
          :class="{
            'bg-bg-inverse': blok.splashDiscountBackgroundColor === 'default',
            'bg-bg-errorStrong': blok.splashDiscountBackgroundColor === 'sale',
          }"
        >
          <div
            v-if="blok.splashDiscountTopText"
            class="mb-2 -mt-2"
            :class="[discountTextSize]"
          >
            {{ blok.splashDiscountTopText }}
          </div>
          <div
            v-if="blok.splashDiscountText"
            class="leading-single"
            :class="[discountNumberSize]"
          >
            {{ blok.splashDiscountText }}
          </div>
        </div>
        <div v-else class="w-full h-full rounded-full overflow-hidden">
          <nuxt-img
            v-if="blok.splashDiscountImage && blok.splashDiscountImage.filename"
            preset="standard"
            loading="lazy"
            :sizes="`sm:100vw desk:${imageSize}`"
            :class="[bottomSplashSize]"
            :src="blok.splashDiscountImage.filename"
            :alt="blok.splashDiscountImage.alt"
          />
        </div>
      </div>
    </div>
    <div
      v-if="blok.preamble || blok.title || blok.tagline || blok.buttonText"
      class="flex flex-col gap-8 pt-16"
      :class="[
        contentMargin, {
          'items-center text-center': !blok.textAlign || blok.textAlign === 'center',
          'items-start': blok.textAlign === 'left',
          'items-end': blok.textAlign === 'right',
        }]"
    >
      <div v-if="blok.preamble" class="type-tagline-sm">
        {{ blok.preamble }}
      </div>
      <div
        v-if="blok.title"
        class=" underline-offset-4 decoration-1"
        :class="[titleSize, {
          'group-hover:underline': isLinked,
        }]"
      >
        {{ blok.title }}
      </div>
      <div
        v-if="blok.tagline"
        class="text-text-subtle"
        :class="[taglineSize]"
      >
        {{ blok.tagline }}
      </div>
      <div
        v-if="blok.buttonText"
        class="btn mt-8"
        :class="{
          'btn--secondary' : blok.buttonStyle === 'secondary',
          'btn--underline' : blok.buttonStyle === 'underline',
        }"
      >
        {{ blok.buttonText }}
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import { PromoBlock } from '~/constants/types/storyblok';
import useVisualAppearancePlugin from '~/composeables/useVisualAppearancePlugin';
import { checkIfVideoFile } from '~/util/imageUtil';
import { AspectRatioKeys } from '~/constants/aspectRatioKeys';
import { isLight } from '~/util/colorUtil';
import { useGlobalContentStore } from '~/store/globalContent';
const globalContent = useGlobalContentStore();

const NuxtLink = resolveComponent('NuxtLink');
const { $sbLinkFix, $sbLinkTarget, $sbLinkCheck } = useNuxtApp();

type Props = {
  blok: PromoBlock;
  size?: 'full' | 'half' | 'third' | 'fourth'; // size if in a promowrapper
  insideGrid?: boolean;
  placement: number;
  aspectRatioMobile?: AspectRatioKeys;
  aspectRatioDesktop?: AspectRatioKeys;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'full',
  aspectRatioMobile: AspectRatioKeys.Square,
  aspectRatioDesktop: AspectRatioKeys.Landscape,
});
const { desktopWidth } = useVisualAppearancePlugin(props);

const isLinked = computed(()=> {
  return props.blok.productLink?.product?.url || $sbLinkCheck(props.blok.link);
});
const linkUrl = computed(()=> {
  if (props.blok.productLink?.product?.url) {
    return `${globalContent.getPrependPathForManualLinks}/${globalContent.domainMap?.productBasePath}/${props.blok.productLink.product?.url}`;
  }
  return $sbLinkFix(props.blok.link);
});
const linkTarget = computed(()=> {
  if (props.blok.productLink?.product?.url) {
    return null;
  }
  return $sbLinkTarget(props.blok.link);
});

const titleSize = computed(()=> {
  switch (props.size) {
    case 'full':
      return 'type-headline-xl desk:type-headline-3xl';
    case 'half':
      return 'type-headline-xl desk:type-headline-2xl';
    case 'third':
      return 'type-headline-xl';
    case 'fourth':
      return 'type-headline-xl';
  }
});
const taglineSize = computed(()=> {
  switch (props.size) {
    case 'full':
      return 'type-base';
    case 'half':
      return 'type-base';
    case 'third':
      return 'type-base';
    case 'fourth':
      return 'type-base';
  }
});

const topSplashPosition = computed(()=> {
  switch (props.size) {
    case 'full':
      return 'desk:top-24 desk:left-24';
  }
});

const bottomSplashPosition = computed(()=> {
  switch (props.size) {
    case 'full':
      return 'desk:bottom-24 desk:right-24';
  }
});

const topSplashSize = computed(()=> {
  switch (props.size) {
    case 'full':
      return 'desk:w-112 desk:h-112';
    case 'half':
      return 'desk:w-96 desk:h-96';
    case 'third':
      return 'desk:w-80 desk:h-80';
  }});

const bottomSplashSize = computed(()=> {
  switch (props.size) {
    case 'full':
      return 'desk:bottom-56 desk:right-56 desk:w-112 desk:h-112';
    case 'half':
      return 'desk:bottom-32 desk:right-32 desk:w-96 desk:h-96';
    case 'third':
      return 'desk:bottom-20 desk:right-20 desk:w-80 desk:h-80';
  }});

const discountTextSize = computed(()=> {
  switch (props.size) {
    case 'full':
      return 'type-tagline desk:-mt-8 desk:type-tagline-sm';
    case 'half':
      return 'type-tagline desk:-mt-8';
    case 'third':
      return 'type-tagline desk:-mt-8';
    case 'fourth':
      return 'type-tagline desk:-mt-8';
  }
});

const discountNumberSize = computed(()=> {
  switch (props.size) {
    case 'full':
      return 'type-headline-md desk:type-headline-xl';
    case 'half':
      return 'type-headline-md desk:type-headline-lg';
    case 'third':
      return 'type-headline-md';
    case 'fourth':
      return 'type-headline-md';
  }
});

const contentMargin = computed(()=> {
  switch (props.size) {
    case 'full':
      return 'desk:pt-20';
    case 'half':
      return 'desk:pt-20';
  }});

const imageSize = computed(()=> {
  switch (props.size) {
    case 'full':
      return '100vw';
    case 'half':
      return '50vw';
    case 'third':
      return '33vw';
    case 'fourth':
      return '25vw';
    default:
      return '1260px';
  }
});

const ratioClass = computed(()=> {
  let output = '';
  if (props.aspectRatioMobile !== '') {
    output += `mobOnly:${props.aspectRatioMobile} `;
  }
  if (props.aspectRatioDesktop !== '') {
    output += `desk:${props.aspectRatioDesktop}`;
  }
  return output;
});

const backgroundColor = computed(()=>{
  if (props.blok.backgroundColor?.color) {
    return props.blok.backgroundColor?.color;
  }
  return '#000';
});

const splashBackgroundColor = computed(()=>{
  if (props.blok.splashBackgroundColor?.color) {
    return props.blok.splashBackgroundColor?.color;
  }
  return '#2E2D2B';
});

</script>

<style scoped lang="postcss">
.spin {
  animation-name: spin;
  animation-duration: 20000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}

.pulse {
  animation-name: pulse;
  animation-duration: 2000ms;
  animation-iteration-count: infinite;
  animation-timing-function: ease-in-out;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}
</style>
